import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Package, AlertTriangle, DollarSign, Receipt, TrendingUp, XCircle, ShoppingBag, Users, Tag, Gift, Percent, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/formatters';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_products: 0,
    low_stock_count: 0,
    today_sales: 0,
    today_invoices_count: 0,
    has_open_register: false,
  });
  const [businessConfig, setBusinessConfig] = useState(null);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [outOfStockProducts, setOutOfStockProducts] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [ofertasDelDia, setOfertasDelDia] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchBusinessConfig();
    fetchLowStockProducts();
    fetchOutOfStockProducts();
    fetchTopProducts();
    fetchOfertasDelDia();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración del negocio');
    }
  };

  const fetchLowStockProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filtrar productos con stock bajo (menos de 10 unidades pero mayor que 0)
      const lowStock = response.data.filter(p => p.existencia > 0 && p.existencia < 10);
      setLowStockProducts(lowStock.slice(0, 5)); // Solo mostrar los primeros 5
    } catch (error) {
      console.error('Error al cargar productos con stock bajo');
    }
  };

  const fetchOutOfStockProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Filtrar productos sin existencia
      const outOfStock = response.data.filter(p => p.existencia === 0);
      setOutOfStockProducts(outOfStock.slice(0, 5)); // Solo mostrar los primeros 5
    } catch (error) {
      console.error('Error al cargar productos sin existencia');
    }
  };

  const fetchTopProducts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/top-products`);
      setTopProducts(response.data);
    } catch (error) {
      console.error('Error al cargar productos más vendidos');
    }
  };

  const fetchOfertasDelDia = async () => {
    try {
      const response = await axios.get(`${API}/ofertas?activas_solo=true`);
      setOfertasDelDia(response.data);
    } catch (error) {
      console.error('Error al cargar ofertas del día');
    }
  };

  const statCards = [
    {
      title: 'Productos Totales',
      value: stats.total_products,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      testId: 'stat-total-products',
    },
    {
      title: 'Sin Existencia',
      value: outOfStockProducts.length,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      testId: 'stat-out-of-stock',
    },
    {
      title: 'Stock Bajo',
      value: lowStockProducts.length,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      testId: 'stat-low-stock',
    },
    {
      title: 'Ventas Hoy',
      value: `RD$ ${stats.today_sales.toLocaleString('es-DO', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      testId: 'stat-today-sales',
    },
    {
      title: 'Facturas Hoy',
      value: stats.today_invoices_count,
      icon: Receipt,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      testId: 'stat-today-invoices',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      {/* Header with Business Info */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-6 md:p-8 text-white">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
          {businessConfig?.logo && (
            <div className="flex-shrink-0">
              <img 
                src={businessConfig.logo} 
                alt="Logo" 
                className="w-20 h-20 md:w-24 md:h-24 object-contain bg-white rounded-lg p-2 shadow-md"
              />
            </div>
          )}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
              {businessConfig?.nombre_comercio || 'LICORSTORE RD'}
            </h1>
            <p className="text-emerald-100 text-sm md:text-base mb-2">
              {businessConfig?.direccion || 'Sistema de Gestión de Licorería'}
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start text-xs md:text-sm">
              {businessConfig?.rnc && (
                <span className="bg-white/20 px-3 py-1 rounded-full">
                  RNC: {businessConfig.rnc}
                </span>
              )}
              {businessConfig?.telefono && (
                <span className="bg-white/20 px-3 py-1 rounded-full">
                  📞 {businessConfig.telefono}
                </span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 text-center md:text-right">
            <p className="text-emerald-100 text-sm mb-1">Bienvenido,</p>
            <p className="font-bold text-lg md:text-xl">{user?.full_name}</p>
            <p className="text-emerald-200 text-xs md:text-sm capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Alert for Cash Register */}
      {!stats.has_open_register && (user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'cajero') && (
        <div
          className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg"
          data-testid="cash-register-alert"
        >
          <div className="flex items-center">
            <AlertTriangle className="text-amber-500 mr-3" size={24} />
            <div>
              <h3 className="font-semibold text-amber-800">Caja Cerrada</h3>
              <p className="text-sm text-amber-700">
                No hay una caja abierta. Por favor, abre la caja antes de realizar ventas.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className="stat-card hover:shadow-lg transition-all"
              data-testid={stat.testId}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs md:text-sm font-medium text-slate-600">{stat.title}</CardTitle>
                <div className={`p-2 md:p-2.5 rounded-lg ${stat.bgColor}`}>
                  <Icon className={stat.color} size={18} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl md:text-3xl font-bold text-slate-800">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Grid de 2 columnas para las listas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Productos Sin Existencia */}
        <Card className="border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <XCircle size={24} />
              Productos Sin Existencia
            </CardTitle>
            <CardDescription>
              Productos que necesitan reabastecimiento urgente
            </CardDescription>
          </CardHeader>
          <CardContent>
            {outOfStockProducts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package size={48} className="mx-auto mb-2 opacity-50" />
                <p>¡Excelente! Todos los productos tienen stock</p>
              </div>
            ) : (
              <div className="space-y-3">
                {outOfStockProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{product.nombre}</p>
                      <p className="text-xs text-slate-600">Código: {product.codigo_interno}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-600">AGOTADO</p>
                      <p className="text-xs text-slate-600">Precio: {formatCurrency(product.precio_venta)}</p>
                    </div>
                  </div>
                ))}
                {outOfStockProducts.length >= 5 && (
                  <p className="text-sm text-center text-slate-500 mt-2">
                    Mostrando los primeros 5 productos
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Productos con Stock Bajo */}
        <Card className="border-2 border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle size={24} />
              Productos con Stock Bajo
            </CardTitle>
            <CardDescription>
              Productos con menos de 10 unidades disponibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            {lowStockProducts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ShoppingBag size={48} className="mx-auto mb-2 opacity-50" />
                <p>No hay productos con stock bajo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{product.nombre}</p>
                      <p className="text-xs text-slate-600">Código: {product.codigo_interno}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">{product.existencia} unidades</p>
                      <p className="text-xs text-slate-600">Precio: {formatCurrency(product.precio_venta)}</p>
                    </div>
                  </div>
                ))}
                {lowStockProducts.length >= 5 && (
                  <p className="text-sm text-center text-slate-500 mt-2">
                    Mostrando los primeros 5 productos
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ofertas Disponibles del Día */}
      {ofertasDelDia.length > 0 && (
        <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Tag size={24} />
              🎉 Ofertas Disponibles del Día
            </CardTitle>
            <CardDescription>
              {ofertasDelDia.length} {ofertasDelDia.length === 1 ? 'promoción activa' : 'promociones activas'} vigente{ofertasDelDia.length === 1 ? '' : 's'} hoy
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ofertasDelDia.map((oferta) => {
                // Determinar el icono y color según tipo
                let Icon = Tag;
                let colorClass = 'bg-red-600';
                let bgClass = 'bg-red-100';
                let borderClass = 'border-red-300';
                let textColorClass = 'text-red-700';
                
                if (oferta.tipo === 'DOS_POR_UNO') {
                  Icon = Gift;
                  colorClass = 'bg-purple-600';
                  bgClass = 'bg-purple-100';
                  borderClass = 'border-purple-300';
                  textColorClass = 'text-purple-700';
                } else if (oferta.tipo === 'PORCENTAJE_DESCUENTO' || oferta.tipo === 'DESCUENTO_CANTIDAD') {
                  Icon = Percent;
                  colorClass = 'bg-green-600';
                  bgClass = 'bg-green-100';
                  borderClass = 'border-green-300';
                  textColorClass = 'text-green-700';
                } else if (oferta.tipo === 'AL_COSTO') {
                  Icon = DollarSign;
                  colorClass = 'bg-blue-600';
                  bgClass = 'bg-blue-100';
                  borderClass = 'border-blue-300';
                  textColorClass = 'text-blue-700';
                } else if (oferta.tipo === 'SEGUNDA_MITAD_PRECIO' || oferta.tipo === 'COMPRA_LLEVA_GRATIS') {
                  Icon = Gift;
                  colorClass = 'bg-orange-600';
                  bgClass = 'bg-orange-100';
                  borderClass = 'border-orange-300';
                  textColorClass = 'text-orange-700';
                }

                // Formatear el texto del tipo
                let tipoTexto = '';
                if (oferta.tipo === 'AL_COSTO') tipoTexto = '💰 Al Costo';
                else if (oferta.tipo === 'PORCENTAJE_DESCUENTO') tipoTexto = `🔥 ${oferta.porcentaje_descuento}% Descuento`;
                else if (oferta.tipo === 'DOS_POR_UNO') tipoTexto = '🎁 2x1';
                else if (oferta.tipo === 'SEGUNDA_MITAD_PRECIO') tipoTexto = '🎯 2da a Mitad';
                else if (oferta.tipo === 'COMPRA_LLEVA_GRATIS') tipoTexto = '🎁 Lleva Gratis';
                else if (oferta.tipo === 'DESCUENTO_CANTIDAD') tipoTexto = `📦 ${oferta.porcentaje_descuento}% en ${oferta.cantidad_minima}+`;
                else if (oferta.tipo === 'COMBO') tipoTexto = '🛒 Combo Especial';
                else if (oferta.tipo === 'DESCUENTO_MONTO_TOTAL') tipoTexto = `💵 ${oferta.porcentaje_descuento}% en compras >RD$${oferta.monto_minimo}`;

                return (
                  <div key={oferta.id} className={`${bgClass} ${borderClass} border-2 rounded-lg p-4`}>
                    <div className="flex items-start gap-3">
                      <div className={`${colorClass} text-white p-2 rounded-lg flex-shrink-0`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold ${textColorClass} mb-1`}>{oferta.nombre}</h4>
                        <p className="text-sm font-semibold text-slate-700 mb-2">{tipoTexto}</p>
                        {oferta.descripcion && (
                          <p className="text-xs text-slate-600 mb-2">{oferta.descripcion}</p>
                        )}
                        {oferta.producto_nombre && (
                          <p className="text-xs text-slate-700">
                            📦 <span className="font-semibold">{oferta.producto_nombre}</span>
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(oferta.fecha_inicio).toLocaleDateString()} - {new Date(oferta.fecha_fin).toLocaleDateString()}
                          </span>
                          {oferta.hora_inicio && oferta.hora_fin && (
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {oferta.hora_inicio} - {oferta.hora_fin}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Productos Más Vendidos del Mes */}
      <Card className="border-2 border-emerald-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-700">
            <TrendingUp size={24} />
            Productos Más Vendidos del Mes
          </CardTitle>
          <CardDescription>
            Top 5 productos con mayor cantidad de ventas este mes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topProducts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ShoppingBag size={48} className="mx-auto mb-2 opacity-50" />
              <p>No hay ventas registradas este mes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex-shrink-0 w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{product.nombre}</p>
                    <p className="text-xs text-slate-600">
                      Código: {product.codigo_interno} | Stock actual: {product.existencia_actual} unidades
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-600">{product.cantidad_vendida}</p>
                    <p className="text-xs text-slate-600 mb-1">unidades vendidas</p>
                    <p className="text-sm font-semibold text-emerald-700">{formatCurrency(product.total_vendido)}</p>
                    <p className="text-xs text-slate-500">total vendido</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
